import { useEffect, useState } from 'react';
import { GetMonitorTrip, MonitorTrip } from '@/apis/monitor';
import { fetchTelemetrySeries } from '@/apis/telemetry';
import useUnauthorize from '@/hooks/useUnauthorize';
import { FOCUS_REFRESH_MS, keepIfSame, startPolling } from './polling';
import { pickTemperatureTrack, TemperatureTrack } from './tripMath';

/** How much history the temperature line shows. */
export const TEMPERATURE_WINDOW_MS = 6 * 60 * 60 * 1000;
/**
 * The trip gives up this soon, well inside a spotlight (10 s), so a request
 * that hangs still leaves time to read the card's fallback before moving on.
 */
export const TRIP_TIMEOUT_MS = 6 * 1000;
/** Readings refresh while a shipment stays in focus. */
export const TELEMETRY_REFRESH_MS = 60 * 1000;

export interface MonitorTripState {
  /** The shipment this data belongs to (never shown for another one). */
  shipmentId: string;
  trip: MonitorTrip | null;
  track: TemperatureTrack | null;
  /**
   * The trip request failed, was refused or timed out: the TV card then
   * shows the destination and dates itself (see HomeSingleShipmentData).
   */
  tripFailed?: boolean;
}

/**
 * Trip and sensor data for the shipment in focus. Returns null on the
 * overview, and drops responses for a shipment the screen has moved past.
 * A shipment with no sensor simply has no temperature line.
 */
export const useMonitorTrip = (
  token: string | null | undefined,
  shipmentId: string | null
): MonitorTripState | null => {
  const { unauthorize } = useUnauthorize();
  const [state, setState] = useState<MonitorTripState | null>(null);

  useEffect(() => {
    setState(shipmentId ? { shipmentId, trip: null, track: null } : null);
    if (!token || !shipmentId) return undefined;
    const update = (signal: AbortSignal, patch: Partial<MonitorTripState>) => {
      if (signal.aborted) return;
      setState((current) =>
        current?.shipmentId === shipmentId ? { ...current, ...patch } : current
      );
    };

    // Both are cancelled when the screen moves to another shipment, and
    // bounded in time; each refreshes one request at a time while the
    // shipment stays in focus (a paused screen).
    // Failed or timed out (an abort from the timeout included). A request the
    // screen has left never marks: the shipment id alone cannot tell it from
    // a new request for the same shipment (rail A, then B, then A again).
    let requestIsCurrent = true;
    const markFailed = () => {
      if (!requestIsCurrent) return;
      setState((current) =>
        current?.shipmentId === shipmentId && !current.trip
          ? { ...current, tripFailed: true }
          : current
      );
    };
    const stopTrip = startPolling(
      (signal) => {
        // The timeout counts the moment it fires: the request itself may
        // only reject after a retry's backoff, which would eat into the time
        // left to read the fallback. (Cleanup aborts too; it is not current
        // then.) A failed refresh keeps the trip already shown.
        signal.addEventListener('abort', markFailed, { once: true });
        return GetMonitorTrip(token, shipmentId, signal)
          .then((res) => {
            const body = res?.data?.data;
            if (!body?.is_success) {
              markFailed();
              return;
            }
            if (signal.aborted) return;
            // An unchanged trip keeps its object: a new one reframes the map.
            setState((current) =>
              current?.shipmentId === shipmentId
                ? {
                    ...current,
                    trip: keepIfSame(current.trip, body.data),
                    tripFailed: false,
                  }
                : current
            );
          })
          .catch((error) => {
            if (!signal.aborted) unauthorize(error);
            markFailed();
          });
      },
      { intervalMs: FOCUS_REFRESH_MS, timeoutMs: TRIP_TIMEOUT_MS }
    );
    const stopReadings = startPolling(
      (signal) => {
        const to = Date.now();
        return fetchTelemetrySeries(
          shipmentId,
          {
            measures: ['temperature', 'latitude'],
            from: to - TEMPERATURE_WINDOW_MS,
            to,
          },
          undefined,
          signal
        )
          .then((series) =>
            update(signal, { track: pickTemperatureTrack(series?.series) })
          )
          .catch(() => {
            // No sensor or no access: the panel just omits the line.
          });
      },
      { intervalMs: TELEMETRY_REFRESH_MS }
    );
    return () => {
      requestIsCurrent = false;
      stopTrip();
      stopReadings();
    };
  }, [token, shipmentId, unauthorize]);

  return state?.shipmentId === shipmentId ? state : null;
};
